import type { SelectionInfo } from '../../extract-paragraphs/selection/selectionTypes';
import { walkCast } from '../helpers/walkCast';
import type { CastInlineContent, CastParagraph, CastRoot } from '../types';

/**
 * Inserts the selection markers into a built Cast tree, given the same
 * `SelectionInfo` the editor produces (selected paragraph ids plus optional
 * start/end character offsets within their paragraphs).
 *
 * - The selection-start marker lands in the first selected paragraph at the
 *   start boundary's offset, or at the very beginning when the selection starts
 *   on a paragraph boundary.
 * - The selection-end marker lands in the last selected paragraph at the end
 *   boundary's offset, or at the very end when the selection ends on a boundary.
 * - A collapsed cursor (both boundaries at the same point) gets a single
 *   selection-start marker and no end marker.
 *
 * Offsets are character counts over the paragraph's concatenated text — the
 * same convention the editor uses when it computes the boundaries, so they line
 * up with the Cast paragraph's runs.
 */
export function applySelectionMarkers(root: CastRoot, selection: SelectionInfo): void {
  const { endBoundary, selectedLocationIds, startBoundary } = selection;
  if (selectedLocationIds.length === 0) return;

  const startId = startBoundary?.paragraphLocationAnchor ?? selectedLocationIds[0];
  const startOffset = startBoundary?.characterOffset ?? 0;
  insertMarker(root, startId, startOffset, { type: 'selectionStart' });

  const isCollapsed =
    startBoundary !== undefined &&
    endBoundary !== undefined &&
    startBoundary.paragraphLocationAnchor === endBoundary.paragraphLocationAnchor &&
    startBoundary.characterOffset === endBoundary.characterOffset;
  if (isCollapsed) return;

  const endId =
    endBoundary?.paragraphLocationAnchor ?? selectedLocationIds[selectedLocationIds.length - 1];
  insertMarker(root, endId, endBoundary?.characterOffset, { type: 'selectionEnd' });
}

/**
 * Finds the paragraph by id and splices the marker in at the character offset
 * (a missing offset means "at the very end"). No-op if the id isn't found.
 */
function insertMarker(
  root: CastRoot,
  paragraphId: string,
  offset: number | undefined,
  marker: CastInlineContent
): void {
  const paragraph = findParagraphById(root, paragraphId);
  if (!paragraph) return;
  const targetOffset = offset ?? totalTextLength(paragraph.children);
  paragraph.children = insertAtTextOffset(paragraph.children, targetOffset, marker).children;
}

function findParagraphById(root: CastRoot, id: string): CastParagraph | undefined {
  let found: CastParagraph | undefined;
  walkCast(root, node => {
    if (found === undefined && node.type === 'paragraph' && node.id === id) found = node;
  });
  return found;
}

function totalTextLength(children: CastInlineContent[]): number {
  let total = 0;
  for (const child of children) {
    if (child.type === 'text') total += child.value.length;
    else if (child.type === 'link') total += totalTextLength(child.children);
  }
  return total;
}

/**
 * Returns a new children array with `marker` inserted after `offset` characters
 * of text, splitting the straddling text run when the offset falls inside it.
 * A link contributes its descendant text to the offset count, and a marker that
 * lands inside a link's text is inserted within the link's own children.
 */
function insertAtTextOffset(
  children: CastInlineContent[],
  offset: number,
  marker: CastInlineContent
): { children: CastInlineContent[]; inserted: boolean } {
  const result: CastInlineContent[] = [];
  let consumed = 0;
  let inserted = false;

  for (const child of children) {
    if (inserted) {
      result.push(child);
      continue;
    }

    if (child.type === 'text') {
      const length = child.value.length;
      if (offset <= consumed) {
        result.push(marker, child);
        inserted = true;
      } else if (offset < consumed + length) {
        const cut = offset - consumed;
        result.push({ ...child, value: child.value.slice(0, cut) }, marker, {
          ...child,
          value: child.value.slice(cut),
        });
        inserted = true;
      } else {
        result.push(child);
        consumed += length;
      }
      continue;
    }

    if (child.type === 'link') {
      const length = totalTextLength(child.children);
      if (offset <= consumed) {
        result.push(marker, child);
        inserted = true;
      } else if (offset < consumed + length) {
        const inner = insertAtTextOffset(child.children, offset - consumed, marker);
        result.push({ ...child, children: inner.children });
        inserted = inner.inserted;
        consumed += length;
      } else {
        result.push(child);
        consumed += length;
      }
      continue;
    }

    result.push(child);
  }

  if (!inserted) result.push(marker);
  return { children: result, inserted };
}
